import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { ThemeSwitcher } from '../components/ThemeSwitcher'
import { roleLabel } from '../lib/format'
import { Page } from '../components/Shell'
import { Button, Chip, Field, Panel, toast } from '../components/ui'

export function Settings() {
  const { user, tenant, signOut } = useAuth()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')

  const change = useMutation({
    mutationFn: () => api('/auth/password', { body: { current, new: next } }),
    onSuccess: () => {
      toast('Password changed — signing you out', 'ok')
      setTimeout(() => void signOut(), 1200)
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const mismatch = confirm.length > 0 && next !== confirm
  const tooShort = next.length > 0 && next.length < 12

  return (
    <Page title="Account settings" subtitle="Your profile, security and display preferences">
      <div className="max-w-2xl space-y-4">
        <Panel title="Profile">
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
            <Row label="Name" value={user?.fullName ?? '—'} />
            <Row label="Email" value={user?.email ?? '—'} />
            <Row
              label="Role"
              value={
                <span className="flex items-center gap-2">
                  {roleLabel(user?.role ?? '')}
                  <Chip tone="muted">Tier {user?.tier}</Chip>
                </span>
              }
            />
            <Row label="Account" value={`${tenant?.name ?? '—'} (${tenant?.kind ?? '—'})`} />
            <Row label="Timezone" value={tenant?.timezone ?? '—'} />
            <Row label="Currency" value={tenant?.currency ?? '—'} />
          </dl>

          <p className="text-[12px] text-[var(--color-ink-4)] mt-5 pt-4 rule-t leading-relaxed">
            Your name, role and fleet access are managed by an administrator on your account. Ask
            them to change these.
          </p>
        </Panel>

        <Panel title="Display">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[13px] text-[var(--color-ink-2)]">Theme</div>
              <div className="text-[11px] text-[var(--color-ink-4)] max-w-sm">
                Dark suits a control room, light a depot office in daylight. System follows
                your device, including its automatic switch at dusk.
              </div>
            </div>
            <ThemeSwitcher />
          </div>
          <p className="text-[11px] text-[var(--color-ink-4)] mt-4 pt-3 rule-t">
            The map has its own style switcher on the map itself — a light interface over a
            satellite basemap is a perfectly good combination.
          </p>
        </Panel>

        <Panel title="Change password">
          {user?.impersonating ? (
            <div
              className="px-3 py-2.5 rounded-[3px] text-[12px]"
              style={{
                color: 'var(--text-warn)',
                background: 'color-mix(in srgb, var(--color-warn) 9%, transparent)',
                border: '1px solid color-mix(in srgb, var(--color-warn) 26%, transparent)',
              }}
            >
              You cannot change a password while in a support session. End the session first.
            </div>
          ) : (
            <form
              className="space-y-3.5"
              onSubmit={(e) => {
                e.preventDefault()
                change.mutate()
              }}
            >
              <Field label="Current password" required>
                <input
                  className="field"
                  type="password"
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                />
              </Field>

              <div className="grid sm:grid-cols-2 gap-3.5">
                <Field
                  label="New password"
                  required
                  hint="At least 12 characters, mixing letters with digits or symbols"
                  error={tooShort ? 'Too short' : undefined}
                >
                  <input
                    className="field"
                    type="password"
                    autoComplete="new-password"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                  />
                </Field>

                <Field label="Confirm" required error={mismatch ? 'Does not match' : undefined}>
                  <input
                    className="field"
                    type="password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                </Field>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!current || !next || mismatch || tooShort || change.isPending}
                >
                  {change.isPending ? 'Changing…' : 'Change password'}
                </Button>
                <span className="text-[11px] text-[var(--color-ink-4)]">
                  You will be signed out everywhere.
                </span>
              </div>
            </form>
          )}
        </Panel>

        <Panel title="Your permissions" subtitle="What this account is allowed to do">
          <div className="flex flex-wrap gap-1.5">
            {(user?.capabilities ?? []).map((c) => (
              <Chip key={c} tone="muted" title={c}>
                {c}
              </Chip>
            ))}
            {!user?.capabilities?.length && (
              <span className="text-[13px] text-[var(--color-ink-4)]">No capabilities granted</span>
            )}
          </div>
          <p className="text-[11px] text-[var(--color-ink-4)] mt-4 leading-relaxed">
            These are checked again on the server for every request — hiding a button never
            substitutes for the permission itself.
          </p>
        </Panel>
      </div>
    </Page>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="label mb-1">{label}</dt>
      <dd className="text-[13px] text-[var(--color-ink)]">{value}</dd>
    </div>
  )
}
